import { motion, useMotionValue, useSpring } from 'motion/react'
import { useCallback, useEffect, useRef } from 'react'

const SPRING = { stiffness: 300, damping: 30, mass: 0.3 }

function computeArrowHead(curvePath: string): string {
	const el = document.createElementNS('http://www.w3.org/2000/svg', 'path')
	el.setAttribute('d', curvePath)
	const totalLen = el.getTotalLength()
	const tip = el.getPointAtLength(totalLen)
	const near = el.getPointAtLength(totalLen - 18)

	const angle = Math.atan2(tip.y - near.y, tip.x - near.x)
	const size = 14
	const spread = 0.5

	const lx = tip.x - size * Math.cos(angle - spread)
	const ly = tip.y - size * Math.sin(angle - spread)
	const rx = tip.x - size * Math.cos(angle + spread)
	const ry = tip.y - size * Math.sin(angle + spread)

	return `M ${tip.x} ${tip.y} L ${lx} ${ly} M ${tip.x} ${tip.y} L ${rx} ${ry}`
}

export function ConnectorArrow({
	fromSelector,
	toSelector,
}: {
	fromSelector: string
	toSelector: string
}) {
	const svgRef = useRef<SVGSVGElement>(null)

	const fromXMotion = useSpring(useMotionValue(0), SPRING)
	const fromYMotion = useSpring(useMotionValue(0), SPRING)
	const cp1xMotion = useSpring(useMotionValue(0), SPRING)
	const cp1yMotion = useSpring(useMotionValue(0), SPRING)
	const cp2xMotion = useSpring(useMotionValue(0), SPRING)
	const cp2yMotion = useSpring(useMotionValue(0), SPRING)
	const toXMotion = useSpring(useMotionValue(0), SPRING)
	const toYMotion = useSpring(useMotionValue(0), SPRING)

	const curveRef = useRef<SVGPathElement>(null)
	const arrowRef = useRef<SVGPathElement>(null)
	const initializedRef = useRef(false)

	useEffect(() => {
		const update = () => {
			const fx = fromXMotion.get()
			const fy = fromYMotion.get()
			const c1x = cp1xMotion.get()
			const c1y = cp1yMotion.get()
			const c2x = cp2xMotion.get()
			const c2y = cp2yMotion.get()
			const tx = toXMotion.get()
			const ty = toYMotion.get()

			const curveD = `M ${fx} ${fy} C ${c1x} ${c1y}, ${c2x} ${c2y}, ${tx} ${ty}`
			curveRef.current?.setAttribute('d', curveD)

			const arrowD = computeArrowHead(curveD)
			arrowRef.current?.setAttribute('d', arrowD)
		}

		const unsubscribers = [
			fromXMotion,
			fromYMotion,
			cp1xMotion,
			cp1yMotion,
			cp2xMotion,
			cp2yMotion,
			toXMotion,
			toYMotion,
		].map(spring => spring.on('change', update))

		return () => unsubscribers.forEach(unsub => unsub())
	}, [fromXMotion, fromYMotion, cp1xMotion, cp1yMotion, cp2xMotion, cp2yMotion, toXMotion, toYMotion])

	const calculate = useCallback(() => {
		const fromEl = document.querySelector(fromSelector)
		const toEl = document.querySelector(toSelector)
		const container = svgRef.current?.parentElement
		if (!fromEl || !toEl || !container) { return }

		const containerRect = container.getBoundingClientRect()
		const fromRect = fromEl.getBoundingClientRect()
		const toRect = toEl.getBoundingClientRect()

		const padding = 20
		const fromX = fromRect.left - containerRect.left - padding
		const fromY = fromRect.top + fromRect.height / 2 - containerRect.top
		const toX = toRect.left - containerRect.left - padding
		const toY = toRect.top - containerRect.top

		const bulgX = Math.min(fromX, toX) - 40
		const cp1x = bulgX - 10
		const cp1y = fromY + (toY - fromY) / 3
		const cp2x = bulgX + 5
		const cp2y = fromY + (toY - fromY) * 2 / 3

		if (!initializedRef.current) {
			initializedRef.current = true
			fromXMotion.jump(fromX)
			fromYMotion.jump(fromY)
			cp1xMotion.jump(cp1x)
			cp1yMotion.jump(cp1y)
			cp2xMotion.jump(cp2x)
			cp2yMotion.jump(cp2y)
			toXMotion.jump(toX)
			toYMotion.jump(toY)
		} else {
			fromXMotion.set(fromX)
			fromYMotion.set(fromY)
			cp1xMotion.set(cp1x)
			cp1yMotion.set(cp1y)
			cp2xMotion.set(cp2x)
			cp2yMotion.set(cp2y)
			toXMotion.set(toX)
			toYMotion.set(toY)
		}
	}, [
		fromSelector,
		toSelector,
		fromXMotion,
		fromYMotion,
		cp1xMotion,
		cp1yMotion,
		cp2xMotion,
		cp2yMotion,
		toXMotion,
		toYMotion,
	])

	useEffect(() => {
		const scrollContainer = document.querySelector<HTMLElement>(
			'[data-editor-scroll-container="true"]',
		)

		let frame = 0
		const scheduleCalculate = () => {
			cancelAnimationFrame(frame)
			frame = requestAnimationFrame(calculate)
		}

		const target = scrollContainer ?? window
		target.addEventListener('scroll', scheduleCalculate, { passive: true })
		window.addEventListener('resize', scheduleCalculate)

		const resizeObserver = new ResizeObserver(scheduleCalculate)
		const fromEl = document.querySelector(fromSelector)
		const toEl = document.querySelector(toSelector)
		if (fromEl) { resizeObserver.observe(fromEl) }
		if (toEl) { resizeObserver.observe(toEl) }

		const commonParent = fromEl?.closest('[id^="resource-"]')?.parentElement
		if (commonParent) { resizeObserver.observe(commonParent) }

		scheduleCalculate()

		return () => {
			target.removeEventListener('scroll', scheduleCalculate)
			window.removeEventListener('resize', scheduleCalculate)
			resizeObserver.disconnect()
			cancelAnimationFrame(frame)
		}
	}, [calculate, fromSelector, toSelector])

	return (
		<svg
			ref={svgRef}
			className="absolute inset-0 w-full h-full pointer-events-none"
			style={{ overflow: 'visible' }}
		>
			<motion.path
				ref={curveRef}
				fill="none"
				stroke="#a855f7"
				strokeWidth="4"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<motion.path
				ref={arrowRef}
				fill="none"
				stroke="#a855f7"
				strokeWidth="4"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</svg>
	)
}
